/**
 * eManifest 申报凭证（含 CLOSE MESSAGE 附属凭证）
 *
 * 设计来源：设计文档 §7.2.1（eManifest）/ §7.3（区块：基本信息 / CCN / 港口仓库 / 参与方 / 汇总 / CONTAINERS）
 * - CCN 就是提单号（D9）：不解析回执、不截断、不列 missing
 * - Volume / Weight 取 containerList 自身字段（与 EmfLinkPayloadAssembler.buildContainers 同源，D8）
 * - QTY / GOODS DESCRIPTION 取该箱匹配货行，一箱多货行同格并列，不相加
 * - CLOSE 凭证仅在存在 Close 数据时作为 attachments 输出
 */
import type {
  CertificateBuilder,
  CertificateContext,
  CertificateData,
  CertificateDict,
  CertificateDocument,
  CertificateSection,
} from '../types'
import { registerCertificateBuilder, CERTIFICATE_TITLES } from './registry'
import {
  DASH,
  FieldCollector,
  billWithScac,
  cargoRowsForContainer,
  dedupeMissing,
  dictCarrierCode9000,
  dictPort,
  filingStatus,
  findParty,
  formatDateTime,
  goodsDescription,
  gridSection,
  latestReceipt,
  logsSection,
  partyAddress,
  pick,
  qtyWithUnit,
  receiptValue,
  rowsOf,
  sumOf,
  tableSection,
  text,
  yesNo,
  type CertRow,
} from './shared'

/** Close 发送状态（与后端 closeSendStatus 取值一致） */
const CLOSE_SEND_STATUS: Record<string, string> = {
  '0': 'Draft',
  '1': 'Sent',
  '2': 'Failed',
  '3': 'Deleted',
}

/** 港口仓库展示：warehouseCode | title */
function warehouseLabel(row: CertRow | undefined): string {
  if (!row) return ''
  return [text(row.warehouseCode), text(row.title)].filter(Boolean).join(' | ')
}

/** CLOSE MESSAGE 附属凭证 */
function buildCloseMessage(
  detail: CertRow,
  missing: string[],
  dict: CertificateDict | undefined
): CertificateDocument | null {
  const hblNo = pick(detail, 'hblNo')
  const closeSendStatus = pick(detail, 'closeSendStatus')
  const closeReceiptTime = formatDateTime(pick(detail, 'closeReceiptTime'))
  const closeCustomsStatus = pick(detail, 'closeCustomsStatus')
  if (!closeSendStatus && !closeReceiptTime && !closeCustomsStatus) return null

  const info = new FieldCollector(missing)
  info
    .add('FILING STATUS', filingStatus(closeSendStatus, code => CLOSE_SEND_STATUS[code] ?? ''))
    .add(
      'MATCHED',
      [closeCustomsStatus, closeReceiptTime].filter(Boolean).join(' · ')
    )
    .add('PREVIOUS CCN', billWithScac(pick(detail, 'previousDeclarantCode'), hblNo))
    .add('CARRIER CODE', pick(detail, 'hblCarrierCode') || dictCarrierCode9000(dict, detail.carrierId))

  // 关联分单 CCN = hbl_carrier_code + hbl_no 拼接，每单一行（数据来自 vas_manifest_close_member）
  const relatedCcnList = rowsOf(detail, 'closeMemberList')
    .map(row => billWithScac(row.carrierCode, row.billNo))
    .filter(Boolean)
  const related = new FieldCollector(missing)
  related.add('RELATED CCN', relatedCcnList.join('\n'))

  return {
    title: 'CLOSE MESSAGE CERTIFICATE',
    issuedAt: closeReceiptTime,
    sections: [
      gridSection(2, info.fields, 'SUBMISSION INFORMATION'),
      gridSection(1, related.fields, 'RELATED HOUSE BILLS'),
    ],
  }
}

export const buildEmfCertificate: CertificateBuilder = (
  context: CertificateContext
): CertificateData => {
  const detail = context.detail ?? {}
  const dict = context.dict
  const receipt = latestReceipt(context.receipts)
  const missing: string[] = []

  const info = new FieldCollector(missing)
  info
    .add(
      'FILING STATUS',
      filingStatus(
        pick(detail, 'customsStatus') || pick(receipt, 'receiptStatus'),
        context.statusLabel,
        pick(receipt, 'sendType')
      )
    )
    .add('TYPE', pick(detail, 'entryType'))
    .add('TRANSMITTED(ET)', formatDateTime(pick(detail, 'sendTime') || pick(receipt, 'sendTime')))

  const ccn = new FieldCollector(missing)
  ccn
    .add('HOUSE BILL CCN', billWithScac(pick(detail, 'hblCarrierCode'), pick(detail, 'hblNo')))
    .add('PRIMARY CCN', billWithScac(pick(detail, 'mblScac'), pick(detail, 'mblNo')))
    .optional('PREVIOUS CCN', pick(detail, 'previousBlNo'))
    .add('CONSOLIDATION', yesNo(detail.consolidation))

  const destWh = findParty(detail, 'WH_DEST')
  const dischargeWh = findParty(detail, 'WH_DISCHARGE')
  const ports = new FieldCollector(missing)
  ports
    .add('PORT OF DESTINATION', text(destWh?.portRelation))
    .add('DESTINATION WAREHOUSE', warehouseLabel(destWh))
    .add('PORT OF DISCHARGE', dictPort(dict, detail.dischargePortId))
    .add('DISCHARGE WAREHOUSE', warehouseLabel(dischargeWh))

  const parties = new FieldCollector(missing)
  parties
    .add('SHIPPER', partyAddress(findParty(detail, 'SHIPPER'), { dict }))
    .add('CONSIGNEE', partyAddress(findParty(detail, 'CONSIGNEE'), { dict }))
    .add('NOTIFY', partyAddress(findParty(detail, 'NOTIFY'), { dict }))
    .optional('DELIVERY ADDRESS', partyAddress(findParty(detail, 'DELIVERY'), { dict }))

  const containers = rowsOf(detail, 'containerList')
  const cargoList = rowsOf(detail, 'cargoList')
  const totalFields = [
    { label: 'TOTAL VOLUME', value: sumOf(containers, 'volume'), unit: ' CBM' },
    { label: 'TOTAL WEIGHT', value: sumOf(containers, 'weight'), unit: ' KGS' },
    { label: 'TOTAL QTY', value: sumOf(cargoList, 'quantity'), unit: '' },
  ].map(field => {
    if (!field.value) missing.push(field.label)
    return {
      label: field.label,
      value: field.value ? `${field.value}${field.unit}` : DASH,
    }
  })

  const containerRows = containers.map(row => {
    const cargo = cargoRowsForContainer(detail, text(row.containerNo))
    const volume = text(row.volume)
    const weight = text(row.weight)
    return [
      text(row.containerNo) || DASH,
      text(row.sealNo) || DASH,
      volume ? `${volume} CBM` : DASH,
      weight ? `${weight} KGS` : DASH,
      qtyWithUnit(cargo, dict) || DASH,
      goodsDescription(cargo) || DASH,
    ]
  })

  const sections: CertificateSection[] = [
    gridSection(3, info.fields),
    gridSection(2, ccn.fields, 'CCN'),
    gridSection(2, ports.fields, 'PORT & WAREHOUSE'),
    gridSection(2, parties.fields, 'PARTY INFORMATION'),
    gridSection(3, totalFields),
    tableSection(
      'CONTAINERS',
      ['Container NO.', 'Seal NO.', 'Volume', 'Weight', 'QTY', 'GOODS DESCRIPTION'],
      containerRows,
      missing
    ),
    logsSection(context.receipts, missing),
  ]

  const data: CertificateData = {
    product: 'EMF',
    title: CERTIFICATE_TITLES.EMF,
    issuedAt: formatDateTime(
      receiptValue(context.receipts, 'receiptTime') ||
        pick(detail, 'receiptTime') ||
        pick(detail, 'dateModified')
    ),
    sections,
    missing: dedupeMissing(missing),
  }

  const closeMessage = buildCloseMessage(detail, missing, dict)
  if (closeMessage) data.attachments = [closeMessage]
  data.missing = dedupeMissing(missing)
  return data
}

registerCertificateBuilder('EMF', buildEmfCertificate)
